import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Send, Loader2, Crown, MessageSquarePlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config/api';
import { trackFeature } from '@/lib/analytics';

const SUGGESTED = [
  "What's the difference between BPC-157 and TB-500 for soft tissue repair?",
  "Explain the mechanism of Semax vs Selank in plain English.",
  "How do bacteriostatic water and saline differ for peptide reconstitution?",
  "What does a half-life of 3 minutes mean for dosing frequency?",
  "Tell me about NAD+ precursors vs bioregulator peptides.",
  "What's a reasonable beginner peptide stack and why?",
];

export default function AIChat() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [messages, setMessages] = useState([]); // {role:'user'|'ai', text}
  const [question, setQuestion] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((u) => setUser(u))
      .finally(() => setLoadingUser(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  const isPremium = user && user.subscription_status && user.subscription_status !== 'free';

  const send = async (textOverride) => {
    const q = (textOverride ?? question).trim();
    if (!q || sending) return;
    if (!isPremium) {
      toast.error('AI chat is a premium feature');
      navigate('/subscription');
      return;
    }
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setQuestion('');
    setSending(true);
    trackFeature('ai-chat', 'ai_query');

    try {
      const res = await fetch(`${API}/ai/ask`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMessages((prev) => [...prev, { role: 'ai', text: data.answer }]);
        if (!sessionId) setSessionId(data.session_id);
      } else if (res.status === 403) {
        toast.error('Premium subscription required for AI chat');
        navigate('/subscription');
      } else {
        const errText = data.detail || 'AI lookup failed';
        setMessages((prev) => [...prev, { role: 'ai', text: `⚠ ${errText}` }]);
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'ai', text: `⚠ Network error: ${e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setQuestion('');
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto h-full flex flex-col" data-testid="ai-chat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> Ask PeptideForge AI
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ask anything about peptides, dosing, recovery, longevity research, or general wellness science.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={newChat} data-testid="new-chat-button">
          <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> New chat
        </Button>
      </div>

      {!loadingUser && !isPremium && (
        <Card className="mb-4 p-5 glass-card border-amber-500/40" data-testid="ai-chat-paywall">
          <div className="flex items-start gap-3">
            <Crown className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold">AI chat is a premium feature</p>
              <p className="text-sm text-muted-foreground mt-1">
                Unlock unlimited AI research conversations, plus the full PeptideForge feature set.
              </p>
              <Button className="mt-3" onClick={() => navigate('/subscription')}>
                <Crown className="h-3.5 w-3.5 mr-1.5" /> Upgrade
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Suggested prompts (only when no messages yet) */}
      {messages.length === 0 && (
        <Card className="mb-4 p-4 glass-card" data-testid="ai-chat-suggestions">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Try asking</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => send(s)}
                disabled={!isPremium || sending}
                className="text-left text-xs md:text-sm px-3 py-2 rounded-lg border border-border hover:border-primary/40 transition-colors disabled:opacity-50"
                data-testid={`suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-4 space-y-3" data-testid="ai-chat-messages">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`ai-msg-${m.role}-${i}`}
          >
            <div
              className={`max-w-[85%] md:max-w-[75%] p-3 md:p-4 rounded-2xl text-sm md:text-base whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-tr-sm'
                  : 'bg-secondary/70 rounded-tl-sm'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="ai-typing">
            <div className="bg-secondary/70 rounded-2xl rounded-tl-sm p-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="mt-2 flex gap-2 items-end" data-testid="ai-chat-composer">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={isPremium ? "Ask me anything…  (Enter to send, Shift+Enter for newline)" : "Premium required"}
          disabled={!isPremium || sending}
          rows={2}
          className="flex-1 resize-none"
          data-testid="ai-chat-input"
        />
        <Button
          size="lg"
          onClick={() => send()}
          disabled={!isPremium || sending || !question.trim()}
          data-testid="ai-chat-send"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <p className="text-[10px] text-muted-foreground mt-2 text-center">
        AI responses are research-only. Not medical advice. Verify before acting on dosing, drug, or supplement information.
      </p>
    </div>
  );
}
